import { Component, DestroyRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { handleDownloadByObjectUrl, isBulkPrice } from '@markant-platform/shared/util-helpers';
import { LocaleService } from '@markant-platform/shared/util-locale';
import { CartridgeVariant, Price, References } from '@markant-platform/cartridge/model';
import { BookingWorkflowData } from '../booking/model/booking.model';
import { CartridgeBookingService } from '../services/cartridge-booking.service';
import { map, Observable, switchMap, take } from 'rxjs';
import { CmsLink } from '@markant-platform/shared/content/model';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { ContentService } from '@markant-platform/shared/content/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'markant-platform-booking-overview',
  templateUrl: './booking-overview.component.html',
  styleUrls: ['./booking-overview.component.scss'],
})
export class BookingOverviewComponent implements OnInit {
  @Input() bookingData!: BookingWorkflowData;
  @Input() isLoading = false;

  @Output() goBack = new EventEmitter<void>();
  @Output() goNext = new EventEmitter<void>();

  localizedCountryName!: Observable<string>;
  hasReferences = false;
  price!: Price;
  setUpCost?: Price;
  isBulkPrice = false;
  serviceGTCsLink?: CmsLink;

  constructor(
    private readonly cartridgesFacade: CartridgesFacade,
    private readonly localeService: LocaleService,
    private readonly cartridgeBookingService: CartridgeBookingService,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly contentService: ContentService,
    private readonly destroyRef: DestroyRef
  ) {
    this.cmsLanguageService.activeCMSLanguage$
      .pipe(
        switchMap(() => this.contentService.getServiceGTCsLink(true)),
        takeUntilDestroyed(),
        map(cmsLinks => cmsLinks[0])
      )
      .subscribe(cmsLink => (this.serviceGTCsLink = cmsLink));

    window.scroll(0, 0);
  }

  ngOnInit(): void {
    this.price = this.getPrice(this.bookingData);
    this.isBulkPrice = isBulkPrice(this.price);
    this.setUpCost = this.getSetUpCost(this.bookingData.variant);
    this.hasReferences = this.determineReferences(this.bookingData.references);
    this.localizedCountryName = this.localeService.getLocalizedCountryName(
      this.bookingData.organization.address.countryCode
    );

    this.cartridgeBookingService.pushTrackingEventData('Step 02 - Summary', this.bookingData, 'Pageview');
  }

  // Downloadlink is not embedded as a native element but rendered by transloco
  // So we need to listen to the click event on the parent element and check if the target is the download link
  @HostListener('click', ['$event.target'])
  addDownloadLinkListener(link: HTMLElement): void {
    if (link.classList.contains('legal-notice-link')) {
      this.openLegalDocument(this.bookingData.cartridge.id);
    }

    if (link.classList.contains('service-gtc-link')) {
      if (this.serviceGTCsLink) {
        window.open(this.serviceGTCsLink.href, this.serviceGTCsLink.target);
      }
    }
  }

  openOfferDocument(priceRequestId: string): void {
    this.cartridgesFacade
      .getOfferDocumentAsObjectUrl(priceRequestId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: handleDownloadByObjectUrl });
  }

  private openLegalDocument(cartridgeId: string): void {
    this.cartridgesFacade
      .getLegalDocumentAsObjectUrlByCartridgeId(cartridgeId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: handleDownloadByObjectUrl });
  }

  private determineReferences(references: References | undefined): boolean {
    return !!references && Object.keys(references).some(key => !!references[key as keyof References]);
  }

  private getPrice(bookingInfo: BookingWorkflowData): Price {
    return bookingInfo.priceRequest?.offerPrice ?? bookingInfo.variant.price;
  }

  private getSetUpCost(variant: CartridgeVariant): Price | undefined {
    return variant.requireIntegrationProject ? variant.setUpCost : undefined;
  }

  triggerNext(): void {
    this.goNext.emit();
  }

  changeServiceGTCAccepted($event: Event): void {
    this.bookingData.serviceGtcAccepted = ($event as CustomEvent<boolean>).detail;
  }
}
